/**
 * Retry utility with exponential backoff and jitter
 * Used for postMessage communication retry logic
 */

// Retry configuration constants
export const MAX_RETRIES = 3;
export const BASE_DELAY_MS = 500;
export const MAX_DELAY_MS = 5000;
export const JITTER_FACTOR = 0.2; // 20% jitter

/**
 * Calculate retry delay with exponential backoff and jitter
 * 
 * @param attempt - Current retry attempt number (0-indexed)
 * @returns Delay in milliseconds with jitter applied
 */
export function calculateRetryDelay(attempt: number): number {
  const exponentialDelay = BASE_DELAY_MS * Math.pow(2, attempt);
  const cappedDelay = Math.min(exponentialDelay, MAX_DELAY_MS);
  const jitter = cappedDelay * JITTER_FACTOR * (Math.random() - 0.5) * 2;
  return Math.max(0, cappedDelay + jitter);
}

/**
 * Check if error is retryable
 * 
 * Retryable errors:
 * - 5xx server errors
 * - Timeout errors
 * 
 * Non-retryable errors (client errors):
 * - ValidationFailedException
 * - 4xx status codes
 * 
 * @param error - Error object from API response
 * @returns true if error should be retried
 */
export function isRetryableStatus(error: any): boolean {
  // Don't retry ValidationFailedException
  if (error?.errorResponse?.statusCode === 'ValidationFailedException') {
    return false;
  }
  
  // Don't retry 4xx client errors
  if (error?.status !== undefined && error.status >= 400 && error.status < 500) {
    return false;
  }
  
  // Retry 5xx server errors
  if (error?.status !== undefined && error.status >= 500) {
    return true;
  }
  
  return false;
}

/**
 * Generic retry wrapper for async operations
 * 
 * @param operation - Async function to retry
 * @param shouldRetry - Function to determine if result should trigger retry
 * @param maxRetries - Maximum number of retry attempts
 * @returns Result of the operation
 * 
 * @example
 * const result = await retryWithBackoff(
 *   () => fetchData(),
 *   (response) => response.status >= 500,
 *   3
 * );
 */
export async function retryWithBackoff<T>(
  operation: () => Promise<T>,
  shouldRetry: (result: T | any) => boolean,
  maxRetries: number = MAX_RETRIES
): Promise<T> {
  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    try {
      const result = await operation();
      return result;
    } catch (error) {
      if (shouldRetry(error) && attempt < maxRetries) {
        await new Promise(resolve => setTimeout(resolve, calculateRetryDelay(attempt)));
        continue;
      }
      throw error;
    }
  }
  
  throw new Error('Retry exhausted');
}